import React from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, Loader2, AlertCircle } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

export default function LoginPage() {
  const { login, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = React.useState("");
  const [password, setPassword] = React.useState("");
  const [error, setError] = React.useState("");
  const [loading, setLoading] = React.useState(false);

  // Already logged in → bounce to their dashboard
  React.useEffect(() => {
    if (user && typeof user === "object") {
      navigate(`/dashboard/${user.role}`, { replace: true });
    }
  }, [user, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    const res = await login(email.trim().toLowerCase(), password);
    setLoading(false);
    if (res.ok) {
      const from = location.state?.from?.pathname;
      navigate(from || `/dashboard/${res.user.role}`, { replace: true });
    } else {
      setError(res.error || "Login failed");
    }
  };

  return (
    <div className="min-h-screen flex items-stretch bg-white" data-testid="login-page">
      {/* Left visual */}
      <div className="hidden lg:flex lg:w-1/2 bg-[#1E4D55] text-white relative overflow-hidden">
        <div
          aria-hidden="true"
          className="absolute inset-0 opacity-10"
          style={{
            backgroundImage:
              "linear-gradient(to right, #ffffff 1px, transparent 1px), linear-gradient(to bottom, #ffffff 1px, transparent 1px)",
            backgroundSize: "48px 48px",
          }}
        />
        <div className="relative z-10 flex flex-col justify-between p-12 w-full">
          <Link
            to="/"
            className="flex items-center font-display font-black tracking-tighter text-white"
            data-testid="login-brand-link"
          >
            <span className="inline-block w-2.5 h-2.5 bg-[#5BB247]" />
            <span className="text-2xl ml-2">raanah<span className="text-[#5BB247]">.</span></span>
          </Link>
          <div>
            <p className="text-[11px] uppercase tracking-[0.22em] font-bold text-white/70">
              Welcome back
            </p>
            <h1 className="font-display mt-4 text-4xl xl:text-5xl tracking-tighter font-black leading-[1]">
              House moves &amp; clearance,
              <br />
              <span className="text-[#5BB247]">paid the right way.</span>
            </h1>
            <p className="mt-6 text-white/70 max-w-md leading-relaxed">
              Sign in to post jobs, accept work, or manage the platform.
            </p>
          </div>
        </div>
      </div>

      {/* Right form */}
      <div className="flex-1 flex items-center justify-center px-6 md:px-12 py-12 bg-[#F5F5F5]">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, ease: "easeOut" }}
          className="w-full max-w-md"
        >
          <Link
            to="/"
            className="lg:hidden inline-flex items-center font-display font-black tracking-tighter text-[#0A0A0A] mb-8"
          >
            <span className="inline-block w-2.5 h-2.5 bg-[#1E4D55]" />
            <span className="text-2xl ml-2">raanah<span className="text-[#1E4D55]">.</span></span>
          </Link>

          <p className="text-[11px] uppercase tracking-[0.22em] font-bold text-[#1E4D55]">
            Sign in
          </p>
          <h2 className="font-display mt-2 text-4xl tracking-tighter font-black text-[#0A0A0A]">
            Welcome back.
          </h2>
          <p className="mt-3 text-[#475569]">
            Don&apos;t have an account?{" "}
            <Link
              to="/register"
              data-testid="login-register-link"
              className="text-[#1E4D55] font-semibold underline underline-offset-4 hover:text-[#163C42]"
            >
              Create one
            </Link>
          </p>

          <form onSubmit={handleSubmit} className="mt-10 space-y-5" data-testid="login-form">
            <div>
              <label className="block text-xs uppercase tracking-[0.18em] font-bold text-[#0A0A0A] mb-2">
                Email
              </label>
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@company.com"
                data-testid="login-email-input"
                className="w-full px-4 py-3.5 bg-white border border-[#0A0A0A]/15 focus:outline-none focus:border-[#1E4D55] focus:ring-2 focus:ring-[#1E4D55]/15 text-sm"
              />
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="block text-xs uppercase tracking-[0.18em] font-bold text-[#0A0A0A]">
                  Password
                </label>
                <Link
                  to="/forgot-password"
                  data-testid="login-forgot-password-link"
                  className="text-[11px] uppercase tracking-[0.14em] font-bold text-[#1E4D55] hover:text-[#163C42]"
                >
                  Forgot?
                </Link>
              </div>
              <input
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                data-testid="login-password-input"
                className="w-full px-4 py-3.5 bg-white border border-[#0A0A0A]/15 focus:outline-none focus:border-[#1E4D55] focus:ring-2 focus:ring-[#1E4D55]/15 text-sm"
              />
            </div>

            {error && (
              <div
                data-testid="login-error"
                className="flex items-start gap-2 text-sm text-[#B91C1C] bg-[#FEE2E2] border border-[#FCA5A5] p-3"
              >
                <AlertCircle size={16} className="mt-0.5 shrink-0" />
                <span>{error}</span>
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              data-testid="login-submit-button"
              className="group w-full inline-flex items-center justify-center gap-2 bg-[#1E4D55] text-white hover:bg-[#163C42] transition-colors px-6 py-4 font-semibold disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {loading ? (
                <>
                  <Loader2 size={18} className="animate-spin" />
                  <span>Signing in…</span>
                </>
              ) : (
                <>
                  <span>Sign in</span>
                  <ArrowUpRight
                    size={18}
                    className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
                  />
                </>
              )}
            </button>
          </form>

          <p className="mt-8 text-xs text-[#475569]">
            Protected by JWT auth · brute-force lockout after 5 attempts
          </p>
        </motion.div>
      </div>
    </div>
  );
}
